/**
 * Static prototype auth service — no backend.
 *
 * Validates against the fixed demo accounts in the mock store and mints a
 * client-side token. Public surface (methods, return types, AuthError) is
 * unchanged so AuthContext and the pages keep working untouched.
 */

import { LoginCredentials, LoginResponse, User } from '../types/auth';
import { clearAllNavigationStates } from '../utils/navigationStorage';
import { DEMO_PASSWORD } from '../mocks/seed';
import { db, delay } from '../mocks/store';

// Mint a JWT-shaped token so authService.isAuthenticated() can parse it.
function mintToken(user: User): string {
  const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = btoa(
    JSON.stringify({
      sub: user.id,
      email: user.email,
      role: user.role,
      exp: Math.floor(Date.now() / 1000) + 7 * 24 * 60 * 60,
    })
  );
  return `${header}.${payload}.mock`;
}

function findActiveUser(email: string): User | undefined {
  return db.users.find(
    (u) => u.email.toLowerCase() === email.toLowerCase() && u.isActive
  );
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    await delay(null, 300);
    const user = findActiveUser(credentials.email);

    if (!user || credentials.password !== DEMO_PASSWORD) {
      throw new AuthError(
        'Credenciales inválidas. Use una cuenta de demostración con la contraseña indicada.',
        'Unauthorized',
        401
      );
    }

    return { user, token: mintToken(user) };
  }

  /**
   * Mint a session right after first-time password setup (mock only — the
   * real backend logs the user in with the password they just created).
   */
  async sessionFromSetup(email: string): Promise<LoginResponse> {
    await delay(null, 200);
    const user = findActiveUser(email);
    if (!user) {
      throw new AuthError('Cuenta no encontrada o inactiva', 'Unauthorized', 401);
    }
    return { user, token: mintToken(user) };
  }

  async logout(): Promise<void> {
    await delay(null, 100);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    clearAllNavigationStates();
  }

  async getProfile(): Promise<User> {
    await delay(null, 150);
    const token = this.getCurrentToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const user = db.users.find((u) => u.id === payload.sub);
        if (user) return user;
      } catch {
        // fall through to stored user
      }
    }
    const stored = this.getCurrentUser();
    if (stored) return stored;
    throw new AuthError('No hay sesión activa', 'Unauthorized', 401);
  }

  async refreshToken(): Promise<LoginResponse> {
    const user = await this.getProfile();
    return { user, token: mintToken(user) };
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('auth_user');
    if (!token || !user) return false;

    try {
      const tokenData = token.split('.');
      if (tokenData.length !== 3) return false;
      const payload = JSON.parse(atob(tokenData[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }

  getCurrentUser(): User | null {
    const user = localStorage.getItem('auth_user');
    return user ? JSON.parse(user) : null;
  }

  getCurrentToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  async casLogin(ticket: string, _service: string): Promise<LoginResponse> {
    await delay(null, 300);
    // Ticket format: "demo-ticket:email" allows selecting any demo account.
    // Plain "demo-ticket" falls back to the default researcher.
    let email = 'investigador@test.com';
    if (ticket.startsWith('demo-ticket:')) {
      email = decodeURIComponent(ticket.slice('demo-ticket:'.length));
    }
    const user = findActiveUser(email);
    if (!user) {
      throw new AuthError('No se pudo iniciar sesión con CAS', 'Unauthorized', 401);
    }
    return { user, token: mintToken(user) };
  }

  async requestPasswordReset(_email: string): Promise<{ message: string }> {
    await delay(null, 200);
    return {
      message:
        'Si la cuenta existe, recibirás un correo con instrucciones para restablecer tu contraseña.',
    };
  }

  async resetPassword(
    _email: string,
    _token: string,
    _password: string
  ): Promise<{ message: string }> {
    await delay(null, 200);
    return { message: 'Contraseña actualizada correctamente.' };
  }

  async validateResetToken(
    _email: string,
    _token: string
  ): Promise<{ valid: boolean; message: string }> {
    await delay(null, 150);
    return { valid: true, message: 'Token válido.' };
  }
}

// Custom error class for authentication errors
class AuthError extends Error {
  constructor(
    message: string,
    public error: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

const authService = new AuthService();
export default authService;
export { AuthError };
